/**
 * @file Wires `formDeriveLabelResolvers` to the three registries a label needs:
 * the ArTable types, the field types and the composite registry. Consumers are the
 * filler surfaces, which have no editor context to read the tables from: the
 * per-form Derived values drawer and the derived-field popover. `FormDerivesPane`
 * calls `formDeriveLabelResolvers` directly with the context's `tableTypes`, so
 * both roads end in one reading and a column target is never named `bom › lineTotal`
 * on one screen and `BOM › Line total` on another. `referenceLabel` is the caller's,
 * because an admin names the reference SLOT while a filler names the record in it.
 * The resolvers are rebuilt every render on purpose: one closed over an older
 * snapshot would answer with an older label. `tableTypes` rides one module-scope
 * collection store, shared by refcount across every mount, and `needsTables` gates
 * the subscription: a caller whose sentence names no table — a form with no derived
 * field, or one derived from another field — reads the collection not at all.
 */
import { useContext, useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';

import { createCollectionStore } from '@/store';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import { useCompositeFieldTypes } from '@/components/CompositeField/useCompositeFieldTypes';
import { formDeriveLabelResolvers } from '@/services/formDerives';
import type { FormDeriveLabelResolvers } from '@/services/formDerives';
import type { ArTableType } from '#types';

const tableTypesStore = createCollectionStore<ArTableType>('tableTypes');

const useFormDeriveLabelResolvers = (
  referenceLabel: (referenceKey: string) => string | undefined,
  needsTables: boolean,
): FormDeriveLabelResolvers => {
  const { fieldTypes } = useContext(FieldTypeContext);
  const compositeFieldTypes = useCompositeFieldTypes();

  useEffect(() => {
    if (!needsTables) return;
    tableTypesStore.connect();
    return () => tableTypesStore.disconnect();
  }, [needsTables]);

  const snap = useSnapshot(tableTypesStore.state);
  const tableTypes = useMemo<ArTableType[]>(
    () =>
      [...snap.docIds].flatMap((docId) => {
        const entry = snap.docs.get(docId);
        return entry === undefined ? [] : [{ ...(entry.data as ArTableType), docId }];
      }),
    [snap.docIds, snap.docs],
  );

  return formDeriveLabelResolvers({
    tableTypes,
    fieldTypes,
    compositeFieldTypes,
    referenceLabel,
  });
};

export { useFormDeriveLabelResolvers };
